import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { handleAPI } from '../../../utils/api/api';

const initialState = {
  roles: [],
  role: {},
  permissions: {},
  totalPage: null,
  totalCount: null,
  isLoading: false,
  error: null,
};

export const getRoles = createAsyncThunk('roles/getRoles', async (params) => {
  const response = await handleAPI('platform/roles', 'get', params);
  return response;
});

export const getRole = createAsyncThunk('roles/getRole', async (id) => {
  const response = await handleAPI(`platform/roles/${id}`, 'get');
  return response;
});

export const editRolePermissions = createAsyncThunk('roles/editPermissions', async (roleId) => {
  const response = await handleAPI(`platform/roles/permissions_template?role_id=${roleId}`, 'get');
  return response;
});

export const editUserPermissions = createAsyncThunk('roles/editUserPermissions', async (userId) => {
  const response = await handleAPI(`platform/roles/permissions_template?user_id=${userId}`, 'get');
  return response;
});

export const rolePermissions = createAsyncThunk('roles/rolePermissions', async (params) => {
  const response = await handleAPI('platform/roles/permissions_template', 'get', params);
  return response;
});

export const addRole = createAsyncThunk('roles/addRole', async (data) => {
  const response = await handleAPI('platform/roles', 'post', '', data);
  return response.data.data;
});

export const updateRole = createAsyncThunk('roles/updateRole', async ({ id, data }) => {
  const response = await handleAPI(`platform/roles/${id}`, 'put', '', data);
  return response;
});

export const cloneRole = createAsyncThunk('roles/cloneRole', async ({ id, data }) => {
  const response = await handleAPI(`platform/roles/${id}/clone`, 'post', '', data);
  return response;
});

export const deleteRole = createAsyncThunk('roles/deleteRole', async (id) => {
  await handleAPI(`platform/roles/${id}`, 'delete');
  return id;
});

const rolesSlice = createSlice({
  name: 'roles',
  initialState,
  reducers: {
    clearCurrentRole: (state) => {
      state.role = {};
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRoles.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getRoles.fulfilled, (state, action) => {
        state.isLoading = false;
        state.roles = action.payload || [];
        state.totalPage = action?.metaData?.total_pages;
        state.totalCount = action?.metaData?.total_count;
      })
      .addCase(getRoles.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      .addCase(getRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(getRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.role = action.payload || {};
      })
      .addCase(getRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      .addCase(rolePermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(rolePermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permissions = action.payload?.data || action.payload || {};
      })
      .addCase(rolePermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      .addCase(editRolePermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(editRolePermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permissions = action.payload?.data || action.payload || {};
      })
      .addCase(editRolePermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      .addCase(editUserPermissions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(editUserPermissions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.permissions = action.payload?.data || action.payload || {};
      })
      .addCase(editUserPermissions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      .addCase(addRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(addRole.fulfilled, (state, action) => {
        state.isLoading = false;
        if (action.payload?.data) {
          state.roles.unshift(action.payload.data);
          state.totalCount = (state.totalCount || 0) + 1;
        }
      })
      .addCase(addRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      .addCase(updateRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(updateRole.fulfilled, (state, action) => {
        state.isLoading = false;
        const updated = action.payload?.data;
        if (updated) {
          const index = state.roles.findIndex((r) => r.id === updated.id);
          if (index !== -1) state.roles[index] = updated;
          if (state.role.id === updated.id) {
            state.role = updated;
          }
        }
      })
      .addCase(updateRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      .addCase(cloneRole.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(cloneRole.fulfilled, (state, action) => {
        state.isLoading = false;
        const created = action.payload?.data?.data || action.payload?.data;
        if (created) {
          state.roles.unshift(created);
          state.totalCount = (state.totalCount || 0) + 1;
        }
      })
      .addCase(cloneRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      })

      .addCase(deleteRole.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteRole.fulfilled, (state, action) => {
        state.isLoading = false;
        state.roles = state.roles.filter((role) => role.id !== action.payload);
        state.totalCount = Math.max((state.totalCount || 1) - 1, 0);
      })
      .addCase(deleteRole.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message;
      });
  },
});

export const { clearCurrentRole, clearError } = rolesSlice.actions;
export default rolesSlice.reducer;
